/** @description 节点进行时 规则编辑 */
import {
  CloseOutlined,
} from '@ant-design/icons';
import { Button, Table, Tooltip } from 'antd';
import { arrayMoveImmutable } from 'array-move';
import moment from 'moment';
import React, { useEffect, useState, useMemo } from 'react';
import _ from 'lodash';
import { DataType, Kind } from './index';
import { useDispatch } from 'umi';
import Box from './box';
import { ActionContent } from './node-default';
import type { ColumnsType } from 'antd/es/table';
import { MenuOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import type { SortableContainerProps, SortEnd } from 'react-sortable-hoc';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';

export const DragHandle = SortableHandle(() => <MenuOutlined style={{ cursor: 'grab', color: '#999' }} />);

const dataInit = {
  onEnter: { action: 'say', text: '', delay: 1 },
  onIdle: { action: 'say', text: '', delay: 1 },
  onReceive: { action: 'execute', code: '匹配字段' },
};

type Props = {
  id: string;
  kind?: Kind;
  data: DataType[];
  saveTableData: (kind: Kind, value: DataType[]) => void;
};

const SortableItem = SortableElement((props: React.HTMLAttributes<HTMLTableRowElement>) => (
  <tr {...props} />
));
const SortableBody = SortableContainer((props: React.HTMLAttributes<HTMLTableSectionElement>) => (
  <tbody {...props} />
));

const App: React.FC<Props> = ({
  kind = 'onIdle',
  data: dataSource = [],
  saveTableData,
  id,
}) => {
  const [currentDeleteKey, setCurrentDeleteKey] = useState<String>();

  useEffect(() => {
    if (currentDeleteKey) handleDelete();
  }, [currentDeleteKey]);

  const dispatch = useDispatch();

  const setDataSource = (data: DataType[]) => {
    saveTableData(kind, _.cloneDeep(data));
  }

  const addTableItem = () => {
    const index = `${kind}-${moment(new Date()).valueOf()}`;
    let item = {
      key: '' + index,
      ...dataInit[kind],
    } as DataType;
    if (!id) item = { ...item, code: '匹配字段' };
    setDataSource([...dataSource, item]);
  };

  const handleDelete = () => {
    const value = dataSource.filter(d => d.key !== currentDeleteKey)
    setDataSource(value);
  }

  const containerStyle = {
    width: '100%',
    overflowX: 'auto',
    scrollbarWidth: 'thin',
  } as React.CSSProperties;
  const addButtonStyle = {
    width: '100%',
    height: '30px',
    border: '1px dashed rgba(0, 0, 0, 0.1)',
  } as React.CSSProperties;

  const contentStyle = {
    cursor: 'pointer',
    color: 'rgb(102, 102, 102)',
    background: 'rgb(242, 235, 233)',
    borderRadius: '4px',
    width: '100%',
    padding: '0px 2px',
    wordBreak: 'break-word',
    textAlign: 'start'
  } as React.CSSProperties;


  const dataIndexMap = {
    sort: {
      title: '排序',
      dataIndex: 'sort',
      width: 60,
      className: 'drag-visible',
      render: () => <DragHandle />
    },
    delete: {
      title: '删除',
      dataIndex: 'delete',
      width: 60,
      render: (_: any, d: DataType) => <CloseOutlined onClick={() => setCurrentDeleteKey(d.key)} />
    },
    content: {
      title: <Tooltip title="可左键长按内容拖动至需要复制的节点中">
        内容<QuestionCircleOutlined style={{marginLeft: 4}} />
      </Tooltip>,
      dataIndex: 'content',
      render: (_: any, d: DataType) => (<Box key={d.key} data={{ item: d, kind }}>
        <div
          style={contentStyle}
          onClick={() => {
            dispatch({
              type: 'open/showActionEditDrawer',
              payload: { d, kind, nodeId: id },
            });
          }}
        >
          {ActionContent(d, kind)}
        </div>
      </Box>)
    },
  };

  const getColumn = (columnItems: ('sort' | 'content' | 'delete')[]) => {
    return columnItems.map((vo) => {
      return dataIndexMap[vo];
    });
  };

  const columnsMap = {
    onEnter: getColumn(['sort', 'content', 'delete']),
    onIdle: getColumn(['sort', 'content', 'delete']),
    onReceive: getColumn(['content']),
  };

  const columns: ColumnsType<DataType> = useMemo(() => {
    return columnsMap[kind]
  }, [kind])

  const BaseTable = CustomTable<DataType>();

  return (
    <>
      <div style={containerStyle}>
        {
          useMemo(() => <BaseTable
            columns={columns}
            data={dataSource}
            setDataSource={setDataSource}
          />, [dataSource])
        }
      </div>
      {['onReceive'].includes(kind) && dataSource.length > 0 ? (
        <div></div>
      ) : (
        <Button style={addButtonStyle} onClick={addTableItem}>
          新增一条
        </Button>
      )}
    </>
  );
};

export interface CustomTableProps<T> {
  data: T[];
  columns: ColumnsType<T>;
  setDataSource: (data: T[]) => void;
}

export const CustomTable = <T extends { key: string },>(): React.FC<CustomTableProps<T>> => ({ data: dataSource, columns, setDataSource }) => {
  const onSortEnd = ({ oldIndex, newIndex }: SortEnd) => {
    if (oldIndex !== newIndex) {
      const newData = arrayMoveImmutable(dataSource.slice(), oldIndex, newIndex).filter(
        el => !!el,
      );
      setDataSource(newData);
    }
  };

  const DraggableContainer = (props: SortableContainerProps) => (
    <SortableBody
      useDragHandle
      disableAutoscroll
      helperClass="row-dragging"
      onSortEnd={onSortEnd}
      {...props}
    />
  );

  const DraggableBodyRow: React.FC<any> = ({ className, style, ...restProps }) => {
    const index = dataSource.findIndex(x => x.key === restProps['data-row-key']);
    return <SortableItem index={index} {...restProps} />;
  };

  return <Table
    pagination={false}
    dataSource={dataSource}
    columns={columns}
    rowKey="key"
    components={{
      body: {
        wrapper: DraggableContainer,
        row: DraggableBodyRow,
      },
    }}
  />
}




export default React.memo(App);
